// getMeetingsSummary — pure derivation of the meetings-section badge/visibility state.
//
// Extracted verbatim from `EditTripModal.js` (the "Business Meetings" header
// logic) as part of the behavior-preserving refactor (issue #240). The source
// rendered the count badge when `editedTrip.meetings.length > 0`, showed the
// "Clear All" button when `editedTrip.meetings.length > 1`, and rendered the
// empty-state block when `editedTrip.meetings.length === 0`. This function
// returns those three booleans plus the raw `count` so the presentation layer
// can drive the same conditionals.
//
// Totality preserved exactly: the source read `editedTrip.meetings.length`
// directly (guaranteed an array by `initializeEditedTrip`); this derivation
// mirrors that with no added guard.
//
// React-free and side-effect-free.
//
// Validates: Requirements 3.1, 3.4, 3.5, 1.6

/**
 * @typedef {Object} MeetingsSummary
 * @property {number} count - The number of meetings on the edited trip.
 * @property {boolean} showBadge - True when the count badge is shown (count > 0).
 * @property {boolean} showClearAll - True when the "Clear All" button is shown (count > 1).
 * @property {boolean} isEmpty - True when the empty-state block is shown (count === 0).
 */

/**
 * Derives the meetings-section badge and button visibility state.
 *
 * @param {Object} editedTrip - The current edited-trip working copy.
 * @returns {MeetingsSummary} The `{ count, showBadge, showClearAll, isEmpty }` summary.
 */
export function getMeetingsSummary(editedTrip) {
  const count = editedTrip.meetings.length;
  return {
    count,
    showBadge: count > 0,
    showClearAll: count > 1,
    isEmpty: count === 0
  };
}

export default getMeetingsSummary;
